import {
  effectiveSuit,
  legalCards,
  partnerOf,
  shuffleDeck,
  teamOf,
  trickWinner,
} from "./cards";
import { londonDateKey, scenarioForDate } from "./daily";
import type {
  Card,
  GameAction,
  GameState,
  HandSummary,
  Mode,
  PublicView,
  Seat,
  Suit,
  Team,
} from "./types";

const HUMAN: Seat = 0;

export function newGame(mode: Mode, locale = "en", now = new Date(), seedOverride?: string): GameState {
  // The Daily identity is the Europe/London date key, never the device-local
  // calendar; it maps to one exact preloaded scenario, or fails closed.
  const dailyDate = mode === "daily" ? londonDateKey(now) : undefined;
  // Daily is a single decisive hand from the published scenario library: the
  // date maps to a stable scenario id and deal seed, never to a random roll.
  const scenario = dailyDate ? scenarioForDate(dailyDate) : null;
  if (dailyDate && !scenario) throw new Error(`No preloaded Daily scenario for London date ${dailyDate}`);
  // seedOverride pins the deal (used by the fixed tutorial hand); rules are
  // untouched — the same seed always reproduces the same deal.
  const seed = scenario ? scenario.seed : seedOverride ?? randomSeed();
  return dealHand(
    {
      version: 2,
      gameId: scenario ? `daily:${scenario.id}` : `${mode}:${seed}`,
      mode,
      seed,
      dailyDate,
      dailyCalendar: scenario ? "london" : undefined,
      dailyScenarioId: scenario?.id,
      assisted: false,
      status: "playing",
      phase: "calling1",
      dealer: 3,
      currentSeat: 0,
      hands: [[], [], [], []],
      kitty: [],
      upcard: { id: "clubs-9", suit: "clubs", rank: "9" },
      callingRound: 1,
      passed: [],
      maker: null,
      trump: null,
      alone: false,
      inactiveSeat: null,
      currentTrick: [],
      tricks: [],
      trickCounts: [0, 0],
      scores: [0, 0],
      handNumber: 0,
      history: [`locale:${locale}`],
      lastHand: null,
      winner: null,
      actionNonce: 0,
    },
    3,
  );
}

function randomSeed(): string {
  if (globalThis.crypto?.getRandomValues) {
    const values = new Uint32Array(2);
    globalThis.crypto.getRandomValues(values);
    return `${values[0].toString(36)}${values[1].toString(36)}`;
  }
  return `${Date.now().toString(36)}-fallback`;
}

export function dealHand(previous: GameState, dealer: Seat): GameState {
  const deck = shuffleDeck(`${previous.seed}:hand:${previous.handNumber}`);
  const hands: [Card[], Card[], Card[], Card[]] = [[], [], [], []];
  const first = nextSeat(dealer);
  for (let index = 0; index < 20; index += 1) {
    const seat = ((first + index) % 4) as Seat;
    hands[seat].push(deck[index]);
  }
  const kitty = deck.slice(20);
  return {
    ...previous,
    status: "playing",
    phase: "calling1",
    dealer,
    currentSeat: first,
    hands,
    kitty,
    upcard: kitty[0],
    callingRound: 1,
    passed: [],
    maker: null,
    trump: null,
    alone: false,
    inactiveSeat: null,
    currentTrick: [],
    tricks: [],
    trickCounts: [0, 0],
    lastHand: null,
    actionNonce: previous.actionNonce + 1,
  };
}

export function nextSeat(seat: Seat): Seat {
  return ((seat + 1) % 4) as Seat;
}

export function nextActiveSeat(state: GameState, seat: Seat): Seat {
  let candidate = nextSeat(seat);
  while (candidate === state.inactiveSeat) candidate = nextSeat(candidate);
  return candidate;
}

export function activeSeats(state: GameState): Seat[] {
  return ([0, 1, 2, 3] as Seat[]).filter((seat) => seat !== state.inactiveSeat);
}

export function reduceGame(state: GameState, action: GameAction): GameState {
  if (state.status === "completed" && action.type !== "NEXT_HAND") return state;
  switch (action.type) {
    case "PASS":
      return pass(state, action.seat);
    case "ORDER_UP":
      return orderUp(state, action.seat, Boolean(action.alone));
    case "CALL_SUIT":
      return callSuit(state, action.seat, action.suit, Boolean(action.alone));
    case "DISCARD":
      return discard(state, action.seat, action.cardId);
    case "PLAY_CARD":
      return playCard(state, action.seat, action.cardId);
    case "COLLECT_TRICK":
      return collectTrick(state);
    case "NEXT_HAND":
      if (state.phase !== "handResult") return state;
      return dealHand({ ...state, handNumber: state.handNumber + 1 }, nextSeat(state.dealer));
  }
}

function assertTurn(state: GameState, seat: Seat, phases: GameState["phase"][]): void {
  if (!phases.includes(state.phase) || state.currentSeat !== seat) {
    throw new Error("Action is not legal in the current turn");
  }
}

function pass(state: GameState, seat: Seat): GameState {
  assertTurn(state, seat, ["calling1", "calling2"]);
  if (state.phase === "calling2" && seat === state.dealer) {
    throw new Error("Stick the Dealer: the dealer must choose a suit");
  }
  const passed = [...state.passed, seat];
  if (state.phase === "calling1" && passed.length === 4) {
    return {
      ...state,
      phase: "calling2",
      callingRound: 2,
      currentSeat: nextSeat(state.dealer),
      passed: [],
      history: [...state.history, `${seat}:pass`, "round:2"],
      actionNonce: state.actionNonce + 1,
    };
  }
  return {
    ...state,
    currentSeat: nextSeat(seat),
    passed,
    history: [...state.history, `${seat}:pass`],
    actionNonce: state.actionNonce + 1,
  };
}

function orderUp(state: GameState, seat: Seat, alone: boolean): GameState {
  assertTurn(state, seat, ["calling1"]);
  const trump = state.upcard.suit;
  const inactiveSeat = alone ? partnerOf(seat) : null;
  const hands = cloneHands(state.hands);
  hands[state.dealer] = [...hands[state.dealer], state.upcard];
  return {
    ...state,
    hands,
    kitty: state.kitty.slice(1),
    maker: seat,
    trump,
    alone,
    inactiveSeat,
    phase: "discard",
    currentSeat: state.dealer,
    history: [...state.history, `${seat}:order:${trump}${alone ? ":alone" : ""}`],
    actionNonce: state.actionNonce + 1,
  };
}

function callSuit(state: GameState, seat: Seat, suit: Suit, alone: boolean): GameState {
  assertTurn(state, seat, ["calling2"]);
  if (suit === state.upcard.suit) throw new Error("Second-round call must choose another suit");
  return startPlay({
    ...state,
    maker: seat,
    trump: suit,
    alone,
    inactiveSeat: alone ? partnerOf(seat) : null,
    history: [...state.history, `${seat}:call:${suit}${alone ? ":alone" : ""}`],
    actionNonce: state.actionNonce + 1,
  });
}

function discard(state: GameState, seat: Seat, cardId: string): GameState {
  assertTurn(state, seat, ["discard"]);
  const hand = state.hands[seat];
  const card = hand.find((entry) => entry.id === cardId);
  if (!card || hand.length !== 6) throw new Error("Dealer must discard one card from six");
  const hands = cloneHands(state.hands);
  hands[seat] = hand.filter((entry) => entry.id !== cardId);
  return startPlay({
    ...state,
    hands,
    kitty: [...state.kitty, card],
    history: [...state.history, `${seat}:discard`],
    actionNonce: state.actionNonce + 1,
  });
}

function startPlay(state: GameState): GameState {
  const leader = state.inactiveSeat === nextSeat(state.dealer)
    ? nextSeat(nextSeat(state.dealer))
    : nextSeat(state.dealer);
  return {
    ...state,
    phase: "playing",
    currentSeat: leader,
    currentTrick: [],
    actionNonce: state.actionNonce + 1,
  };
}

function playCard(state: GameState, seat: Seat, cardId: string): GameState {
  assertTurn(state, seat, ["playing"]);
  if (!state.trump) throw new Error("Trump must be set before play");
  if (seat === state.inactiveSeat) throw new Error("Inactive alone partner cannot play");
  const hand = state.hands[seat];
  const card = hand.find((entry) => entry.id === cardId);
  if (!card) throw new Error("Card is not in this seat's hand");
  const legal = legalCards(hand, state.currentTrick, state.trump);
  if (!legal.some((entry) => entry.id === cardId)) {
    const lead = effectiveSuit(state.currentTrick[0].card, state.trump);
    throw new Error(`Must follow effective suit: ${lead}`);
  }
  const hands = cloneHands(state.hands);
  hands[seat] = hand.filter((entry) => entry.id !== cardId);
  const currentTrick = [...state.currentTrick, { seat, card }];
  const complete = currentTrick.length === activeSeats(state).length;
  return {
    ...state,
    hands,
    currentTrick,
    phase: complete ? "trickResult" : "playing",
    currentSeat: complete ? seat : nextActiveSeat(state, seat),
    history: [...state.history, `${seat}:play:${card.id}`],
    actionNonce: state.actionNonce + 1,
  };
}

function collectTrick(state: GameState): GameState {
  if (state.phase !== "trickResult" || !state.trump) throw new Error("No complete trick to collect");
  const winner = trickWinner(state.currentTrick, state.trump).seat;
  const leadSuit = effectiveSuit(state.currentTrick[0].card, state.trump);
  const trick = { cards: state.currentTrick, winner, leadSuit };
  const tricks = [...state.tricks, trick];
  const trickCounts: [number, number] = [...state.trickCounts] as [number, number];
  trickCounts[teamOf(winner)] += 1;
  if (tricks.length === 5) {
    return finishHand({ ...state, tricks, trickCounts, currentTrick: [] });
  }
  return {
    ...state,
    tricks,
    trickCounts,
    currentTrick: [],
    phase: "playing",
    currentSeat: winner,
    actionNonce: state.actionNonce + 1,
  };
}

function finishHand(state: GameState): GameState {
  if (state.maker === null || !state.trump) throw new Error("Cannot score without a maker and trump");
  const makerTeam = teamOf(state.maker);
  const makerTricks = state.trickCounts[makerTeam];
  const points: [number, number] = [0, 0];
  let reason: HandSummary["reason"];
  if (makerTricks < 3) {
    points[(1 - makerTeam) as Team] = 2;
    reason = "euchred";
  } else if (state.alone && makerTricks === 5) {
    points[makerTeam] = 4;
    reason = "lone-march";
  } else if (state.alone) {
    points[makerTeam] = 1;
    reason = "lone-made";
  } else if (makerTricks === 5) {
    points[makerTeam] = 2;
    reason = "march";
  } else {
    points[makerTeam] = 1;
    reason = "made";
  }
  const scores: [number, number] = [
    state.scores[0] + points[0],
    state.scores[1] + points[1],
  ];
  // Daily is one decisive hand: it completes immediately, and the winner is
  // the team this hand's points went to (points are always one-sided).
  const winner: Team | null = state.mode === "daily"
    ? ((points[0] > 0 ? 0 : 1) as Team)
    : scores[0] >= 10 ? 0 : scores[1] >= 10 ? 1 : null;
  const lastHand: HandSummary = {
    maker: state.maker,
    makerTeam,
    trump: state.trump,
    alone: state.alone,
    tricks: state.trickCounts,
    points,
    reason,
  };
  return {
    ...state,
    scores,
    winner,
    lastHand,
    status: winner === null ? "playing" : "completed",
    phase: winner === null ? "handResult" : "completed",
    history: [...state.history, `hand:${reason}:${points.join("-")}`],
    actionNonce: state.actionNonce + 1,
  };
}

function cloneHands(hands: GameState["hands"]): GameState["hands"] {
  return hands.map((hand) => [...hand]) as GameState["hands"];
}

export function publicView(state: GameState, seat: Seat): PublicView {
  const legalCardIds =
    state.phase === "playing" && state.currentSeat === seat && state.trump
      ? legalCards(state.hands[seat], state.currentTrick, state.trump).map((card) => card.id)
      : [];
  return {
    version: 1,
    seat,
    team: teamOf(seat),
    ownHand: [...state.hands[seat]],
    handCounts: state.hands.map((hand) => hand.length) as [number, number, number, number],
    dealer: state.dealer,
    currentSeat: state.currentSeat,
    phase: state.phase,
    callingRound: state.callingRound,
    upcard: state.upcard,
    trump: state.trump,
    maker: state.maker,
    alone: state.alone,
    inactiveSeat: state.inactiveSeat,
    currentTrick: [...state.currentTrick],
    completedTricks: state.tricks.map((trick) => ({ ...trick, cards: [...trick.cards] })),
    trickCounts: [...state.trickCounts],
    scores: [...state.scores],
    legalCardIds,
    actionNonce: state.actionNonce,
  };
}

export function isHumanTurn(state: GameState): boolean {
  return state.currentSeat === HUMAN && ["calling1", "calling2", "discard", "playing"].includes(state.phase);
}

export function matchMetrics(state: GameState): { hands: number; calls: number; callsMade: number; euchres: number } {
  let maker: Seat | null = null;
  let hands = 0;
  let calls = 0;
  let callsMade = 0;
  let euchres = 0;
  for (const event of state.history) {
    const call = event.match(/^([0-3]):(?:order|call):/);
    if (call) {
      maker = Number(call[1]) as Seat;
      if (maker === HUMAN) calls += 1;
      continue;
    }
    if (!event.startsWith("hand:")) continue;
    hands += 1;
    const reason = event.split(":")[1];
    if (maker === HUMAN && reason !== "euchred") callsMade += 1;
    if (maker !== null && maker % 2 === 1 && reason === "euchred") euchres += 1;
    maker = null;
  }
  return { hands, calls, callsMade, euchres };
}

export function validateState(state: GameState): boolean {
  if (!state || typeof state !== "object") return false;
  if (state.version !== 2 || !["quick", "daily", "coach"].includes(state.mode)) return false;
  if (!["playing", "completed"].includes(state.status)) return false;
  if (!["calling1", "calling2", "discard", "playing", "trickResult", "handResult", "completed"].includes(state.phase)) return false;
  if (![0, 1, 2, 3].includes(state.dealer) || ![0, 1, 2, 3].includes(state.currentSeat)) return false;
  if (!Array.isArray(state.hands) || state.hands.length !== 4 || state.hands.some((hand) => !Array.isArray(hand))) return false;
  if (!Array.isArray(state.kitty) || !Array.isArray(state.currentTrick) || !Array.isArray(state.tricks)) return false;
  if (!Array.isArray(state.scores) || state.scores.length !== 2 || state.scores.some((score) => !Number.isInteger(score) || score < 0)) return false;
  if (!Array.isArray(state.trickCounts) || state.trickCounts.length !== 2 || state.trickCounts.some((count) => !Number.isInteger(count) || count < 0 || count > 5)) return false;
  if (!Number.isInteger(state.actionNonce) || state.actionNonce < 0 || !Number.isInteger(state.handNumber) || state.handNumber < 0) return false;
  if (state.trump !== null && !["clubs", "diamonds", "hearts", "spades"].includes(state.trump)) return false;
  if (state.dailyCalendar !== undefined && state.dailyCalendar !== "london") return false;
  if (state.dailyScenarioId !== undefined && typeof state.dailyScenarioId !== "string") return false;
  if (state.maker !== null && ![0, 1, 2, 3].includes(state.maker)) return false;
  if (state.inactiveSeat !== null && ![0, 1, 2, 3].includes(state.inactiveSeat)) return false;
  if (state.currentTrick.length > (state.alone ? 3 : 4) || state.tricks.length > 5) return false;
  const cards = [
    ...state.hands.flat(),
    ...state.kitty,
    ...state.currentTrick.map((entry) => entry.card),
    ...state.tricks.flatMap((trick) => trick.cards.map((entry) => entry.card)),
  ];
  if (cards.some((card) =>
    !card
    || typeof card.id !== "string"
    || !["clubs", "diamonds", "hearts", "spades"].includes(card.suit)
    || !["9", "10", "J", "Q", "K", "A"].includes(card.rank)
  )) return false;
  const ids = cards.map((card) => card.id);
  if (ids.length !== 24 || new Set(ids).size !== 24) return false;
  if (state.alone && (state.maker === null || state.inactiveSeat !== partnerOf(state.maker))) return false;
  if (!state.alone && state.inactiveSeat !== null) return false;
  if (state.currentTrick.some((entry) => ![0, 1, 2, 3].includes(entry.seat))) return false;
  if (state.tricks.some((trick) =>
    ![0, 1, 2, 3].includes(trick.winner)
    || !["clubs", "diamonds", "hearts", "spades"].includes(trick.leadSuit)
  )) return false;
  if (state.tricks.some((trick) => trick.cards.length !== (state.alone ? 3 : 4))) return false;
  return true;
}
